import React, { useState } from 'react';
import { Button, Chip, Divider, Stack, Typography } from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { TextField } from 'new/elements/TextField';
import { useConfirmDialog } from 'new/elements/ConfirmDialog';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { showAlert } from 'new/stores/showAlert/action';
import {
  useFetchAdminOrgMembers,
  useUpdateAdminOrg,
  useUpdateAdminOrgMembers,
} from 'new/stores/adminOrgs/adminOrgs.api';
import { AdminOrg, OrgMember } from 'new/stores/adminOrgs/adminOrgs.types';
import { AdminRole } from '../../RoleBanner';
import { GuardedControl } from '../../GuardedControl';

interface AdminsTabProps {
  org: AdminOrg;
  role: AdminRole;
}

function AdminRow({
  member,
  isOwner,
  disabled,
  onTransfer,
}: {
  member: OrgMember;
  isOwner: boolean;
  disabled: boolean;
  onTransfer: (member: OrgMember) => void;
}) {
  const isDeactivated = member.org_membership_status !== 'ACTIVE';

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.75 }}>
      <Stack direction="row" gap="12px" alignItems="center">
        <Typography variant="body2" fontWeight={500}>
          {member.full_name}
        </Typography>
        <Typography variant="body3" color="text.secondary">
          {member.email}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" label={isOwner ? 'Owner' : 'Admin'} />
        {!isOwner && (
          <GuardedControl
            disabled={disabled || isDeactivated}
            tooltip={
              isDeactivated
                ? 'This user is deactivated and cannot become the org owner.'
                : undefined
            }
          >
            <Button size="small" variant="text" onClick={() => onTransfer(member)}>
              Transfer ownership
            </Button>
          </GuardedControl>
        )}
      </Stack>
    </Stack>
  );
}

export function AdminsTab({ org, role }: AdminsTabProps) {
  const dispatch = useAppDispatch();
  const { data: members = [] } = useFetchAdminOrgMembers(org.id);
  const [updateMembers, { isLoading: isInviting }] = useUpdateAdminOrgMembers();
  const [updateOrg] = useUpdateAdminOrg();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const disabled = role !== 'full';

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');

  const admins = members.filter(
    (m) => m['is_admin?'] || m.email === org.owner?.email || m.id === org.owner?.id,
  );

  const onInvite = () => {
    if (!inviteEmail || !inviteName) return;
    updateMembers({
      orgId: org.id,
      members: [{ email: inviteEmail, full_name: inviteName, admin: true }],
    }).then((result) => {
      if ('error' in result) {
        dispatch(showAlert({ title: `Failed to invite ${inviteEmail}`, severity: 'error' }));
        return;
      }
      setInviteEmail('');
      setInviteName('');
    });
  };

  const onTransfer = (member: OrgMember) => {
    confirm({
      title: 'Transfer ownership',
      message: `Make ${member.full_name} (${member.email}) the owner of "${org.name}"? This takes effect immediately and cannot be undone from here.`,
      confirmText: 'Transfer ownership',
      variant: 'warning',
      onConfirm: () => updateOrg({ id: org.id, body: { owner_id: member.id } }),
    });
  };

  return (
    <Stack spacing={3}>
      <div>
        <Typography variant="subtitle2" gutterBottom>
          Invite admin
        </Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            placeholder="Email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <TextField
            placeholder="Full name"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
          />
          <GuardedControl disabled={disabled || isInviting || !inviteEmail || !inviteName}>
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={onInvite}>
              Invite
            </Button>
          </GuardedControl>
        </Stack>
      </div>

      <div>
        <Typography variant="subtitle2" gutterBottom>
          Org admins
        </Typography>
        <Stack divider={<Divider />}>
          {admins.map((member) => (
            <AdminRow
              key={member.id}
              member={member}
              isOwner={member.id === org.owner?.id || member.email === org.owner?.email}
              disabled={disabled}
              onTransfer={onTransfer}
            />
          ))}
          {!admins.length && (
            <Typography variant="body2" color="text.secondary">
              No admins found.
            </Typography>
          )}
        </Stack>
      </div>

      {ConfirmDialogComponent}
    </Stack>
  );
}
